import type { CreateTarget } from 'actions';
import { EditModel } from 'edit/EditModel';
import { waitDialog } from 'dialogs';
import { getLock, type Lock } from 'api/locks/endpoints/getLock';
import { postLock } from 'api/locks/endpoints/postLock';
import { putLock } from 'api/locks/endpoints/putLock';
import { EditableLock } from './EditableLock';
import { EditLockDialog } from './EditLockDialog';

export type ShowEditLockArgs = { readonly mode: 'create'; readonly createIn?: CreateTarget } | { readonly mode: 'edit'; readonly id: number };

// Persist the entity: POST when it's a new record, PUT (addressed by the
// pre-edit identity) when editing an existing one. Returns the saved record so
// the EditModel can reinitialize the entity from it — the POST/PUT shapes are
// structurally assignable to the single-GET `Lock`.
const save = (entity: EditableLock): Promise<Lock> => {
  const body = entity.toSave();
  const original = entity.original;
  return original === null ? postLock(body) : putLock(original, body);
};

/**
 * Open the lock create / edit dialog and wait for the user to save or cancel.
 * Resolves to `true` if a save succeeded (caller should refresh its list view),
 * `undefined` if cancelled.
 *
 * For edit it fetches the record by id (boundary-validated), so it works from
 * the Workspace — which never populates the list slice — and never opens on a
 * loosely-cast store row. A missing/invalid record throws, surfaced inline by
 * the dialog's load-error state rather than opening on a degraded record.
 */
export const showEditLock = (args: ShowEditLockArgs): Promise<true | undefined> => {
  const initialize: () => Promise<Lock | null> =
    args.mode === 'create'
      ? async () => null
      : async () => {
          const lock = await getLock(args.id);
          if (!lock) throw new Error('Lock not found; the list may be out of date. Please refresh.');
          return lock;
        };

  const model = new EditModel(initialize, save, (loaded) => new EditableLock(loaded, { createIn: args.mode === 'create' ? args.createIn : undefined }), {
    initialMode: args.mode === 'edit' ? 'edit' : 'create',
    successMessage: args.mode === 'edit' ? 'Lock updated' : 'Lock created',
  });

  return waitDialog<true>((ok, cancel) => <EditLockDialog model={model} onSaved={() => ok(true)} onClose={cancel} />);
};
